// Espace candidat — port de rec5-panel.jsx (parcours cliquable en
// nœuds carrés SVG, sections colorées r4, colonne contact/docs/
// conformité). Phase 3: appel de qualification scripté et entretiens/
// scorecards à révélation séquentielle (EvalSections); l'offre reste
// « phase 4 ». Les documents sont des liens (Drive…) — pas de stockage
// de fichiers.
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { ENTITIES, REC_STAGES } from '@pointage/shared';
import type {
  RecApplicationDTO, RecInterviewerDTO, RecRequisitionDTO, RecRoleDTO, RecStageId,
} from '../../api/hooks.js';
import {
  RecAv, RecBand, RecChain, RecDaysPill, RecSal, recContrat, recDays, recFmtDate,
  recFmtMoney, recOfferAction, recOfferOf, recOfferStOf, recStageOf,
} from './common.js';
import { InterviewsSection, recFmtDT, recJuryAvg } from './EvalSections.js';
import { CallSectionV2, OfferGatesSection, TestSection, VerifSection, recDispoLbl, useRecSt } from './V2Sections.js';
import { BarPill, Radar, V2Ic, fmt1 } from './V2Ui.js';
import { useMe } from '../../api/hooks.js';
import { OfferActionButtons, OfferDiff, type OfferActions } from './Offres.js';
import { recOfferPdfUrl } from '../../api/hooks.js';
import { CandidateFiles } from './CandidateFiles.js';
import { FileViewer, drivePreviewUrl, type ViewerDoc } from './FileViewer.js';

// ── iconographie SVG maison (R5Ic) ──────────────────────────
export function R5Ic({ k, s = 18, w = 1.8 }: { k: string; s?: number; w?: number }) {
  const P: Record<string, React.ReactNode> = {
    NOUVELLE: <><path d="M4 13l2.5-7h11L20 13v5.5H4z" /><path d="M4 13h4.5a3.5 3.5 0 007 0H20" /></>,
    PRESEL: <><path d="M10 6.5h10M10 12h10M10 17.5h6" /><path d="M4 5.5l1.4 1.4 2.4-2.8" /><path d="M4 11l1.4 1.4 2.4-2.8" /><circle cx="5.5" cy="17.5" r="1.6" /></>,
    APPEL: <path d="M5.5 4h2.8l1.8 4.6-2.3 1.5a12.5 12.5 0 005.9 5.9l1.5-2.3L20 15.5v2.8a2.2 2.2 0 01-2.4 2.2A16.5 16.5 0 013.3 6.4 2.2 2.2 0 015.5 4z" />,
    ENTRETIEN: <><rect x="9" y="3" width="6" height="11.5" rx="3" /><path d="M5.5 11.5a6.5 6.5 0 0013 0" /><path d="M12 18v3" /></>,
    FINALISTE: <><path d="M5.5 21V4" /><path d="M5.5 4.5h12.5l-2.6 4 2.6 4H5.5" /></>,
    OFFRE: <><rect x="3.5" y="5" width="17" height="14" rx="2.5" /><path d="M4 7.5l8 5.5 8-5.5" /></>,
    RECRUTE: <><circle cx="10" cy="8" r="3.6" /><path d="M4 20a6.2 6.2 0 0112 0" /><path d="M15.5 12l2 2 3.5-4" /></>,
    notes: <><path d="M4.5 19.5l.9-3.6L16.6 4.7a2.05 2.05 0 012.9 2.9L8.3 18.8z" /><path d="M14.8 6.5l2.9 2.9" /></>,
    user: <><circle cx="12" cy="8" r="3.6" /><path d="M5.5 20a6.5 6.5 0 0113 0" /></>,
    doc: <><path d="M6.5 3.5h7l4 4v13h-11z" /><path d="M13.5 3.5v4h4" /></>,
    shield: <><path d="M12 3.5l7 2.5v5.5c0 4.4-3 7.7-7 9-4-1.3-7-4.6-7-9V6z" /><path d="M9 11.5l2 2 4-4.5" /></>,
    repeat: <><path d="M4 8.5a6 6 0 0110.5-3.5L17 7.5" /><path d="M17 3.5v4h-4" /><path d="M20 15.5A6 6 0 019.5 19L7 16.5" /><path d="M7 20.5v-4h4" /></>,
    check: <path d="M5 12.5l4.5 4.5L19 7" />,
    TEST: <><path d="M9 3.5h6" /><path d="M10 3.5v5.2L4.8 17.6A2 2 0 006.5 20.5h11a2 2 0 001.7-2.9L14 8.7V3.5" /><path d="M7.5 14.5h9" /></>,
    VERIF: <><path d="M12 3.5l7 2.5v5.5c0 4.4-3 7.7-7 9-4-1.3-7-4.6-7-9V6z" /><path d="M9 11.5l2 2 4-4.5" /></>,
  };
  return (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={w} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {P[k] ?? null}
    </svg>
  );
}

type Sec = 'presel' | 'appel' | 'test' | 'score' | 'verif' | 'offre' | 'notes';
const SEC_OF_STAGE: Record<string, Sec> = {
  NOUVELLE: 'presel', PRESEL: 'presel', APPEL: 'appel', TEST: 'test',
  ENTRETIEN: 'score', FINALISTE: 'offre', OFFRE: 'offre', RECRUTE: 'offre',
};
const SECS: Record<Sec, { c: string; ic: string; t: string; h: string }> = {
  presel: { c: '#0792a8', ic: 'PRESEL', t: 'Présélection CV', h: 'pass / fail — seuls les critères éliminatoires bloquent' },
  appel: { c: '#2a6fdb', ic: 'APPEL', t: 'Appel de qualification', h: 'script SHIFTIN v1.0 — 7 temps, clôture calculée' },
  test: { c: '#0bb5ab', ic: 'TEST', t: 'Cas pratique rémunéré', h: 'brief figé, rendu noté par dimension — sous le seuil on n’avance pas' },
  score: { c: '#7037ef', ic: 'ENTRETIEN', t: 'Entretiens & scorecards', h: 'note par dimension, preuve écrite pour 1, 2 et 5 — révélation séquentielle' },
  verif: { c: '#0a9d72', ic: 'VERIF', t: 'Vérification', h: 'avant l’offre, jamais après — références, documents, intégrité' },
  offre: { c: '#ff5e11', ic: 'OFFRE', t: 'Offre & décision', h: 'portes : barre · cas pratique · vérification · veto du manager' },
  notes: { c: '#0a9d72', ic: 'notes', t: 'Notes & activité', h: 'notes internes + journal complet du parcours' },
};

// ── parcours en nœuds (Rec5Journey) ─────────────────────────
function Journey({ app, role, sel, onSel }: {
  app: RecApplicationDTO;
  role: RecRoleDTO | undefined;
  sel: Sec;
  onSel: (s: Sec) => void;
}) {
  const idx = REC_STAGES.findIndex((s) => s.id === app.stage);
  const crits = role?.templates.presel ?? [];
  const passed = crits.filter((c) => app.presel[c.id] === true).length;
  const { n: nSc } = recJuryAvg(app, role?.templates.grid ?? []);
  const nextItw = app.interviews.find((i) => i.status === 'PLANIFIE');
  const SUB: Record<string, string> = {
    NOUVELLE: app.candidate.consentAt.slice(5).split('-').reverse().join('/'),
    PRESEL: `${passed}/${crits.length} critères`,
    APPEL: app.call
      ? app.call.completedAt
        ? `✓ ${app.call.verdict === 'go' ? 'avancer' : app.call.verdict}`
        : app.call.scheduledAt ? recFmtDT(app.call.scheduledAt).split(' · ')[0]! : 'à planifier'
      : '—',
    TEST: app.test ? (app.test.status === 'NOTE' ? `${fmt1(app.test.score)}/5` : app.test.status === 'RENDU' ? 'à noter' : app.test.status === 'ENVOYE' ? 'en cours' : 'à envoyer') : '—',
    ENTRETIEN: nSc > 0 ? `${nSc} scorecard${nSc > 1 ? 's' : ''}`
      : nextItw ? recFmtDT(nextItw.scheduledAt).split(' · ')[0]! : '—',
    FINALISTE: app.gates.bar?.state === 'ok' ? 'barre ✓' : app.gates.bar?.state === 'ko' ? (app.gates.overridden ? 'dérogation' : 'sous la barre') : idx >= 5 ? 'décision' : '—',
    OFFRE: (() => {
      const o = recOfferOf(app);
      return o ? `v${o.version} · ${recOfferStOf(o.status).lbl.split(' — ')[0]!.toLowerCase()}` : '—';
    })(),
    RECRUTE: app.offers.some((o) => o.status === 'ACCEPTEE') ? 'offre acceptée' : '—',
  };
  return (
    <div className="r3-journey">
      {REC_STAGES.map((s, i) => {
        const cls = `${i < idx ? ' done' : ''}${i === idx ? ' now' : ''}${i <= idx ? ' past' : ''}${SEC_OF_STAGE[s.id] === sel ? ' sel' : ''}`;
        return (
          <button
            key={s.id} className={`r3-node${cls}`}
            style={{ '--nc': s.c, animationDelay: `${i * 55}ms` } as React.CSSProperties}
            onClick={() => onSel(SEC_OF_STAGE[s.id]!)}
            title={`Afficher : ${s.lbl}`}
          >
            <span className="dot">{i < idx ? <R5Ic k="check" s={15} w={2.4} /> : <R5Ic k={s.id} s={17} />}</span>
            <span className="lb">{s.lbl}</span>
            <span className="sub">{SUB[s.id]}</span>
          </button>
        );
      })}
      <button
        className={`r3-node r5-noteN${sel === 'verif' ? ' sel' : ''}${app.gates.verif.ok ? ' done past' : ''}`}
        style={{ '--nc': '#0a9d72', animationDelay: '360ms' } as React.CSSProperties}
        onClick={() => onSel('verif')} title="Vérification"
      >
        <span className="dot"><R5Ic k="VERIF" s={16} /></span>
        <span className="lb">Vérif.</span>
        <span className="sub">{app.gates.verif.done}/3</span>
      </button>
      <button
        className={`r3-node r5-noteN${sel === 'notes' ? ' sel' : ''}`}
        style={{ '--nc': '#0a9d72', animationDelay: '390ms' } as React.CSSProperties}
        onClick={() => onSel('notes')} title="Notes & activité"
      >
        <span className="dot"><R5Ic k="notes" s={16} /></span>
        <span className="lb">Notes</span>
        <span className="sub">{app.notes.length || '—'}</span>
      </button>
    </div>
  );
}

// ── présélection (RecPresel) ────────────────────────────────
function Presel({ app, role, canEdit, busy, onSet, onValidate }: {
  app: RecApplicationDTO;
  role: RecRoleDTO | undefined;
  canEdit: boolean;
  busy: boolean;
  onSet: (critId: string, value: boolean | null) => void;
  onValidate: () => void;
}) {
  const crits = (role?.templates.presel ?? []) as Array<{ id: string; lbl: string; elim?: boolean }>;
  if (!crits.length) return <div className="rec-emptybox">Ce rôle n’a pas de critères de présélection au catalogue.</div>;
  // v2 — seuls les critères éliminatoires bloquent ; un indicatif en FAIL est noté.
  const elims = crits.filter((c) => c.elim !== false);
  const done = elims.filter((c) => app.presel[c.id] === true).length;
  const failed = elims.some((c) => app.presel[c.id] === false);
  const soft = crits.filter((c) => c.elim === false && app.presel[c.id] === false);
  const atPresel = app.stage === 'NOUVELLE' || app.stage === 'PRESEL';
  return (
    <div>
      <p style={{ fontSize: 12, color: 'var(--ink3)', margin: '0 0 6px' }}>
        Checklist binaire — pass / fail, pas un score. Seuls les critères <b>éliminatoires</b> bloquent ({done}/{elims.length} validés).
      </p>
      {crits.map((c) => {
        const v = app.presel[c.id];
        return (
          <div key={c.id} className="rec-crit">
            <span className="lbl">{c.lbl}</span>
            <span className="cated-pf" style={c.elim !== false ? { background: 'var(--ink)', color: 'var(--card)' } : undefined}>{c.elim !== false ? 'ÉLIMINATOIRE' : 'INDICATIF'}</span>
            <div className="rec-pf">
              <button className={`ok${v === true ? ' on' : ''}`} disabled={!canEdit || busy} onClick={() => onSet(c.id, v === true ? null : true)}>PASS</button>
              <button className={`ko${v === false ? ' on' : ''}`} disabled={!canEdit || busy} onClick={() => onSet(c.id, v === false ? null : false)}>FAIL</button>
            </div>
          </div>
        );
      })}
      {soft.length > 0 && !failed && (
        <div className="rv2-ovwarn" style={{ marginTop: 12 }}>⚑ {soft.map((c) => c.lbl).join(', ')} — critère indicatif, noté au dossier. La présélection reste validable.</div>
      )}
      {atPresel && canEdit && (
        <div style={{ display: 'flex', gap: 10, marginTop: 16, alignItems: 'center', flexWrap: 'wrap' }}>
          <button className="rec-go" style={failed ? { background: 'var(--bad)' } : undefined} disabled={busy || (done < elims.length && !failed)} onClick={onValidate}>
            {failed ? 'Rejeter — critère éliminatoire' : 'Valider → appel de qualification'}
          </button>
          {done < elims.length && !failed && (
            <span style={{ fontSize: 11, color: 'var(--ink4)', fontWeight: 600 }}>Cochez les {elims.length} critères éliminatoires pour avancer</span>
          )}
        </div>
      )}
    </div>
  );
}

// ── timeline (RecTimeline) ──────────────────────────────────
function fmtAt(iso: string): string {
  const d = new Date(iso);
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')} · ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}
function Timeline({ app }: { app: RecApplicationDTO }) {
  const rows: Array<{ at: string; t: string; ic: string; tx: React.ReactNode }> = [];
  rows.push({
    at: `${app.candidate.consentAt}T00:00:00`,
    t: app.candidate.consentAt.slice(5).split('-').reverse().join('/'),
    ic: '📥',
    tx: <span>Candidature reçue — source <b>{app.source}</b>{app.referredBy && <> (par {app.referredBy})</>}, consentement horodaté</span>,
  });
  const preselN = Object.values(app.presel).filter(Boolean).length;
  if (preselN > 0) rows.push({ at: rows[0]!.at, t: '', ic: '✓', tx: <span>Présélection CV — <b>{preselN}</b> critères validés</span> });
  for (const h of app.stageHistory.slice(1)) {
    const st = recStageOf(h.stage);
    rows.push({ at: h.at, t: fmtAt(h.at), ic: h.stage === 'REJETE' ? '✕' : h.stage === 'VIVIER' ? '♻' : '→', tx: <span>Passage en <b>{st.lbl}</b> — {h.how} · <b>{h.by}</b></span> });
  }
  for (const m of app.emails) {
    rows.push({ at: m.at, t: fmtAt(m.at), ic: '✉️', tx: <span>Email {m.status === 'failed' ? <b style={{ color: 'var(--badc)' }}>en échec</b> : 'envoyé'} — <b>{m.subject}</b></span> });
  }
  if (app.call?.completedAt) {
    rows.push({
      at: app.call.completedAt, t: fmtAt(app.call.completedAt), ic: '📞',
      tx: <span>Appel de qualification réalisé{app.call.caller && <> par <b>{app.call.caller}</b></>} — verdict <b>{app.call.verdict === 'go' ? 'avancer' : app.call.verdict}</b></span>,
    });
  } else if (app.call?.scheduledAt) {
    rows.push({
      at: app.call.scheduledAt, t: fmtAt(app.call.scheduledAt), ic: '📞',
      tx: <span>Appel de qualification <b>planifié</b>{app.call.caller && <> — {app.call.caller}</>}</span>,
    });
  }
  for (const itw of app.interviews) {
    rows.push({
      at: itw.scheduledAt, t: fmtAt(itw.scheduledAt), ic: '🎙',
      tx: <span><b>{itw.type}</b> · {itw.mode} — {itw.status === 'PLANIFIE' ? 'planifié' : itw.status === 'REALISE' ? 'réalisé' : itw.status === 'ANNULE' ? 'annulé' : 'no-show'}</span>,
    });
    for (const s of itw.scorecards ?? (itw.myScorecard ? [itw.myScorecard] : [])) {
      rows.push({
        at: s.at, t: fmtAt(s.at), ic: '📊',
        tx: <span>Scorecard soumise par <b>{s.author}</b> — verdict <b>{s.verdict === 'oui' ? 'oui' : s.verdict === 'reserve' ? 'oui avec réserve' : 'non'}</b></span>,
      });
    }
    if (itw.amJuror && !itw.myScorecard && itw.scorecardCount > 0) {
      rows.push({
        at: itw.scheduledAt, t: '', ic: '🙈',
        tx: <span>{itw.scorecardCount} scorecard{itw.scorecardCount > 1 ? 's' : ''} masquée{itw.scorecardCount > 1 ? 's' : ''} — soumettez la vôtre pour les révéler</span>,
      });
    }
  }
  rows.sort((a, b) => a.at.localeCompare(b.at));
  return (
    <div className="rec-feed">
      {rows.map((r, i) => (
        <div key={i} className="rec-feedrow">
          <span className="t">{r.t}</span>
          <span className="ic">{r.ic}</span>
          <p>{r.tx}</p>
        </div>
      ))}
    </div>
  );
}

// ── panneau ─────────────────────────────────────────────────
export function CandidatePanel({ app, req, role, dark, canEdit, canSal, meId, busy, onClose, onAdvance, onPresel, onPreselValidate, onReject, onVivier, onRestore, onEmail, onNote, onPlanItw, onItwStatus, onScorecard, offerAct }: {
  app: RecApplicationDTO;
  req: RecRequisitionDTO;
  role: RecRoleDTO | undefined;
  dark: boolean;
  canEdit: boolean;
  canSal: boolean;
  meId: string;
  busy: boolean;
  onClose: () => void;
  onAdvance: (stage: RecStageId) => void;
  onPresel: (critId: string, value: boolean | null) => void;
  onPreselValidate: () => void;
  onReject: (forced?: string) => void;
  onVivier: () => void;
  onRestore: () => void;
  onEmail: () => void;
  onNote: (text: string) => void;
  onPlanItw: () => void;
  onItwStatus: (id: string, status: 'REALISE' | 'ANNULE' | 'NO_SHOW') => void;
  onScorecard: (itwId: string, scores: Record<string, number>, verdict: 'oui' | 'reserve' | 'non', comment: string, evidence: Record<string, string>) => void;
  offerAct: OfferActions;
}) {
  const me = useMe();
  const perms = me.data?.permissions ?? [];
  const canOffre = perms.includes('recrutement.offre');
  const isBoss = me.data?.user.role === 'BOSS';
  const st = useRecSt();
  const stage = recStageOf(app.stage);
  const idx = REC_STAGES.findIndex((s) => s.id === app.stage);
  const curSec: Sec = SEC_OF_STAGE[app.stage] ?? 'presel';
  const [sel, setSel] = useState<Sec>(curSec);
  const [note, setNote] = useState('');
  useEffect(() => { setSel(SEC_OF_STAGE[app.stage] ?? 'presel'); }, [app.stage]);
  const ent = ENTITIES.find((e) => e.id === req.entityId);
  const c = app.candidate;
  const next = idx >= 0 ? REC_STAGES[idx + 1] : undefined;
  const closed = app.stage === 'REJETE' || app.stage === 'VIVIER';
  const S = SECS[sel];
  const sendNote = () => { if (note.trim()) { onNote(note.trim()); setNote(''); } };
  const { avg: juryAvg, n: nSc } = recJuryAvg(app, role?.templates.grid ?? []);
  const secStatus: Record<Sec, React.ReactNode> = {
    presel: <span className="r4-chip">{(role?.templates.presel ?? []).filter((x) => app.presel[x.id] === true).length}/{(role?.templates.presel ?? []).length} critères</span>,
    appel: app.call
      ? <span className="r4-chip">{app.call.completedAt
          ? `✓ réalisé — ${app.call.verdict === 'go' ? 'avancer' : app.call.verdict}`
          : app.call.scheduledAt ? `planifié ${recFmtDT(app.call.scheduledAt)}` : 'à planifier'}</span>
      : <span className="r4-chip">—</span>,
    score: (
      <>
        {juryAvg != null && <span className="r4-chip">★ {juryAvg}/5</span>}
        <span className="r4-chip">{nSc} scorecard{nSc > 1 ? 's' : ''}</span>
      </>
    ),
    offre: (() => {
      const o = recOfferOf(app);
      return o
        ? <span className="r4-chip" style={{ color: recOfferStOf(o.status).c }}>v{o.version} · {recOfferStOf(o.status).lbl}</span>
        : <span className="r4-chip">{app.stage === 'FINALISTE' ? 'à construire' : '—'}</span>;
    })(),
    notes: <span className="r4-chip">{app.notes.length} note{app.notes.length > 1 ? 's' : ''}</span>,
    test: <span className="r4-chip">{app.test ? (app.test.status === 'NOTE' ? `${fmt1(app.test.score)}/5` : app.test.status === 'RENDU' ? 'rendu — à noter' : app.test.status === 'ENVOYE' ? 'envoyé' : 'à envoyer') : '—'}</span>,
    verif: <span className="r4-chip">{app.gates.verif.done}/3 conditions</span>,
  };
  const [linkView, setLinkView] = useState<ViewerDoc | null>(null);
  const docLinks: Array<[string, string | null]> = [['CV', c.cvUrl], ['Portfolio', c.portfolioUrl], ['Lettre', c.letterUrl]];

  return createPortal(
    <div className={`recapp rec-portal${dark ? ' theme-dark' : ''}`}>
      <div className="rec-ov" onClick={onClose}></div>
      <div className="r3-panel" style={{ '--sc': stage.c } as React.CSSProperties}>
        <div className="r3-hero">
          <div className="r3-idrow">
            <RecAv app={app} big />
            <div>
              <div className="r3-name">{c.firstName} {c.lastName}</div>
              <div className="r3-role">{req.roleLabel} · {ent?.name}{c.expSummary ? ` · ${c.expSummary}` : ''}</div>
              <div className="r3-chips">
                <span className="rec-est" style={{ background: `color-mix(in srgb,${stage.c} 15%,transparent)`, color: stage.c }}><i></i>{stage.lbl}</span>
                {!closed && <RecDaysPill since={app.stageSince} suffix=" dans l’étape" />}
                <span className="rec-tag" style={{ fontSize: 10, padding: '4px 8px', textTransform: 'capitalize' }}>{app.source}{app.referredBy ? ` · ${app.referredBy}` : ''}</span>
                {c.tags.map((t) => <span key={t} className="rec-tag" style={{ fontSize: 10, padding: '4px 8px' }}>{t}</span>)}
                {juryAvg != null && !app.gates.masked && <BarPill bar={app.gates.bar} overridden={app.gates.overridden} st={st} />}
                {app.test?.status === 'NOTE' && <span className={`rec-vd ${app.gates.test.pass ? 'oui' : 'non'}`} style={{ marginLeft: 0 }}>🧪 {fmt1(app.test.score)}/5 cas</span>}
                {app.stage === 'REJETE' && app.rejectMotif && <span className="rec-stpill bad">{app.rejectMotif}</span>}
              </div>
            </div>
            <div className="r3-actions">
              {canEdit && !closed && next && next.id !== 'RECRUTE' && app.stage !== 'OFFRE' && (
                next.id === 'OFFRE'
                  ? (!recOfferOf(app) && (canSal && canOffre && app.gates.open
                      ? <button className="rec-go" disabled={busy} onClick={() => { offerAct.onBuild(app.id, null); }}>Construire l’offre →</button>
                      : <button className="rec-go" onClick={() => setSel('offre')}>Portes de l’offre →</button>))
                  : next.id === 'TEST' || app.stage === 'TEST'
                    ? null
                    : next.id === 'FINALISTE' && !(app.interviews.some((i) => (i.scorecards ?? []).length > 0 || i.myScorecard))
                      ? null
                      : <button className="rec-go" disabled={busy} onClick={() => onAdvance(next.id as RecStageId)}>Avancer → {next.lbl}</button>
              )}
              {canEdit && !closed && <button className="rec-ghost" disabled={busy} onClick={onEmail}><R5Ic k="OFFRE" s={14} w={2} /> Email</button>}
              {canEdit && !closed && <button className="rec-ghost" disabled={busy} onClick={onVivier}><R5Ic k="repeat" s={14} w={2} /> Vivier</button>}
              {canEdit && !closed && <button className="rec-ghost danger" disabled={busy} onClick={() => onReject()}>Rejeter</button>}
              {canEdit && app.stage === 'VIVIER' && <button className="rec-go" disabled={busy} onClick={onRestore}>Repêcher → Nouvelle</button>}
              <button className="rec-x" onClick={onClose}>✕</button>
            </div>
          </div>
          <Journey app={app} role={role} sel={sel} onSel={setSel} />
        </div>
        <div className="r4-body">
          <div className="r4-left">
            {app.gates.jury && !app.gates.masked && nSc > 0 && (
              <div className="r4-card" style={{ background: 'var(--card)' }}>
                <h4 style={{ color: 'var(--shfc)' }}><V2Ic k="target" s={14} w={2} /> Face à la barre</h4>
                <Radar s={app.gates.jury} st={st} color={c.avColor} size={210} />
                <div style={{ display: 'flex', justifyContent: 'center', marginTop: 4 }}><BarPill bar={app.gates.bar} overridden={app.gates.overridden} st={st} /></div>
                <div className="rec-hint" style={{ textAlign: 'center', marginTop: 6 }}>Pointillés = barre (Perf. {fmt1(st.barPerf)}, autres {fmt1(st.barMin)})</div>
              </div>
            )}
            <div className="r4-card id">
              <h4><R5Ic k="user" s={14} w={2} /> Contact</h4>
              <div className="rec-meta">
                <div className="r"><span>Email</span><b>{c.email}</b></div>
                {c.phone && <div className="r"><span>Téléphone</span><b>{c.phone}</b></div>}
                {c.city && <div className="r"><span>Ville</span><b>{c.city}</b></div>}
                {c.linkedin && <div className="r"><span>LinkedIn</span><b><a href={c.linkedin} target="_blank" rel="noreferrer">profil ↗</a></b></div>}
                {c.expSummary && <div className="r"><span>Expérience</span><b>{c.expSummary}</b></div>}
                {app.call?.pretSet && (
                  <div className="r"><span>Prétentions</span>{canSal && app.call.pretAmount != null ? <b className="rec-sal">{recFmtMoney(app.call.pretAmount)} {app.call.pretType ?? ''}</b> : <RecSal band={null} />}</div>
                )}
                {(app.call?.dispoDate || app.call?.dispoDelay) && (
                  <div className="r"><span>Disponibilité</span><b>{[recDispoLbl(app.call.dispoDelay), app.call.dispoDate ? `le ${recFmtDate(app.call.dispoDate)}` : null].filter(Boolean).join(' · ')}</b></div>
                )}
                {app.call?.commune && <div className="r"><span>Trajet</span><b>{app.call.commune}{app.call.trajetMin != null ? ` · ${app.call.trajetMin} min` : ''}{app.call.vehicule ? ' · véhiculé·e' : ''}</b></div>}
              </div>
            </div>
            <div className="r4-card doc">
              <h4><R5Ic k="doc" s={14} w={2} /> Documents</h4>
              <CandidateFiles app={app} canEdit={canEdit && !closed} dark={dark} />
              {docLinks.some(([, u]) => u) && (
                <div className="rec-docbtns" style={{ marginTop: 8 }}>
                  {docLinks.filter(([, u]) => u).map(([lbl, url]) => (
                    <a key={lbl} className="rec-docbtn" href={url!} target="_blank" rel="noreferrer"
                      onClick={drivePreviewUrl(url) ? (e) => { e.preventDefault(); setLinkView({ title: `${lbl} — lien Drive`, url: url!, mime: null }); } : undefined}>
                      {lbl} {drivePreviewUrl(url) ? '👁' : '↗'}
                    </a>
                  ))}
                </div>
              )}
              {linkView && <FileViewer doc={linkView} dark={dark} onClose={() => setLinkView(null)} />}
              {!docLinks.some(([, u]) => u) && !(app.candidate.files ?? []).length && (
                <div style={{ fontSize: 10.5, color: 'var(--ink4)', fontWeight: 600, marginTop: 8 }}>Aucune pièce — téléversez le CV (PDF, 8 Mo max) ou ajoutez un lien à la création.</div>
              )}
            </div>
            {req.outcomes90.length > 0 && (
              <div className="r4-card warnc">
                <h4><V2Ic k="flag" s={14} w={2} /> Objectifs du poste</h4>
                <div style={{ display: 'grid', gap: 6 }}>{req.outcomes90.map((o, i) => <div key={i} style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink2)', lineHeight: 1.45 }}>{i + 1}. {o}</div>)}</div>
              </div>
            )}
            {app.prevApplications.length > 0 && (
              <div className="r4-card warnc">
                <h4><R5Ic k="repeat" s={14} w={2} /> Autres candidatures</h4>
                {app.prevApplications.map((p) => (
                  <div key={p.applicationId} className="rec-prev" style={{ marginTop: 0, marginBottom: 6 }}>
                    <b>{p.roleLabel} · {p.year}</b>
                    <span>{recStageOf(p.stage).lbl}{p.rejectMotif ? ` — ${p.rejectMotif}` : ''}</span>
                  </div>
                ))}
              </div>
            )}
            <div className="r4-card okc">
              <h4><R5Ic k="shield" s={14} w={2} /> Conformité</h4>
              <div className="rec-consent">
                Consentement du <b>{c.consentAt.split('-').reverse().join('/')}</b> (loi 18-07) · conservation 24 mois après clôture · droit à la suppression via RH.
              </div>
            </div>
          </div>
          <div className="r4-main">
            <div className="rec-view" key={sel}>
              <section className="r4-sec cur" style={{ '--c4': S.c } as React.CSSProperties}>
                <div className="r4-sech">
                  <span className="ic"><R5Ic k={S.ic} s={17} /></span>
                  <div><h3>{S.t}</h3><div className="hint">{S.h}</div></div>
                  <div className="st">{secStatus[sel]}{sel === curSec && !closed && <span className="r4-cur">étape en cours</span>}</div>
                </div>
                <div className="r4-secb">
                  {sel === 'presel' && (
                    <Presel app={app} role={role} canEdit={canEdit && !closed} busy={busy} onSet={onPresel} onValidate={onPreselValidate} />
                  )}
                  {sel === 'appel' && (
                    <CallSectionV2 app={app} req={req} role={role} canEdit={canEdit && !closed} canSal={canSal} meName={me.data?.user.name ?? ''} onStop={() => onReject()} />
                  )}
                  {sel === 'test' && (
                    <TestSection app={app} role={role} canEdit={canEdit && !closed} canSal={canSal} onReject={() => onReject('Échec cas pratique')} />
                  )}
                  {sel === 'verif' && (
                    <VerifSection app={app} canOffre={canOffre} dark={dark} onForceReject={() => onReject('Incohérence à la vérification')} />
                  )}
                  {sel === 'score' && (
                    <InterviewsSection
                      app={app} role={role} meId={meId} canEdit={canEdit && !closed} busy={busy}
                      onPlanClick={onPlanItw} onStatus={onItwStatus} onScorecard={onScorecard}
                    />
                  )}
                  {sel === 'offre' && (() => {
                    const o = recOfferOf(app);
                    if (!o) {
                      return app.stage === 'FINALISTE' || app.stage === 'ENTRETIEN' ? (
                        <OfferGatesSection app={app} req={req} isBoss={isBoss} canOffre={canOffre && canSal && !closed} dark={dark}
                          onVerif={() => setSel('verif')} onBuild={() => offerAct.onBuild(app.id, null)} />
                      ) : (
                        <div className="rec-lockbox">
                          <span className="big"><R5Ic k="OFFRE" s={30} w={1.4} /></span>
                          Pas encore d’offre — le candidat doit d’abord être finaliste.
                        </div>
                      );
                    }
                    const st = recOfferStOf(o.status);
                    const prev = app.offers.length > 1 ? app.offers[app.offers.length - 2]! : null;
                    const hint = recOfferAction(o);
                    return (
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                          <span className="r6-st" style={{ background: st.c, marginLeft: 0 }}>{st.lbl}</span>
                          <span className="rec-vchip">v{o.version}</span>
                          {canSal && o.salaire != null
                            ? <b className="rec-sal" style={{ fontSize: 17 }}>{recFmtMoney(o.salaire)}</b>
                            : <RecSal band={null} />}
                          <span className="rec-hint">· {recContrat(req)} · essai {o.essai} · début {recFmtDate(o.startDate)}</span>
                        </div>
                        {canSal && o.salaire != null && req.band && <RecBand band={req.band} val={o.salaire} />}
                        {o.refusalComment && o.status === 'BROUILLON' && (
                          <div className="rec-refusnote">✕ Refusée en interne — « {o.refusalComment} ». Ajustez puis re-soumettez.</div>
                        )}
                        {prev?.counterDetails && (
                          <div className="rec-consent" style={{ margin: '10px 0 0' }}>
                            ↩ Contre-proposition sur v{prev.version} : <b>{canSal ? prev.counterDetails : 'détails restreints — RH & direction'}</b>
                          </div>
                        )}
                        {prev && <OfferDiff a={prev} b={o} canSal={canSal} />}
                        {o.approvals.length > 0 && <RecChain approvals={o.approvals} />}
                        {hint && <div className="rec-qwait" style={{ marginTop: 10 }}>⏳ {hint}</div>}
                        <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap', alignItems: 'center' }}>
                          <OfferActionButtons app={app} o={o} canEdit={canEdit} canSal={canSal} busy={busy} act={offerAct} compact />
                          {canSal && <a className="rec-ghost sm" href={recOfferPdfUrl(o.id)} target="_blank" rel="noreferrer">📄 PDF v{o.version}</a>}
                        </div>
                      </div>
                    );
                  })()}
                  {sel === 'notes' && (
                    <div className="r4-two">
                      <div>
                        {canEdit && (
                          <div className="r3-note-in">
                            <input
                              value={note} placeholder="Ajouter une note… (Entrée)"
                              onChange={(e) => setNote(e.target.value)}
                              onKeyDown={(e) => { if (e.key === 'Enter') sendNote(); }}
                            />
                            <button className="rec-go" style={{ padding: '0 14px' }} disabled={!note.trim() || busy} onClick={sendNote}>+</button>
                          </div>
                        )}
                        {app.notes.map((n) => (
                          <div key={n.id} className="r3-note">{n.text}<div className="by">{n.by} · {fmtAt(n.at)}</div></div>
                        ))}
                        {!app.notes.length && (
                          <div style={{ fontSize: 11.5, color: 'var(--ink4)', fontWeight: 600, padding: '6px 2px' }}>
                            Aucune note — visible équipe recrutement uniquement, jamais le candidat.
                          </div>
                        )}
                      </div>
                      <div className="r3-feedcard"><Timeline app={app} /></div>
                    </div>
                  )}
                </div>
              </section>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
